
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, parseISO, subDays, format } from "date-fns";

// Types for inventory data
export type InventoryItem = {
  id: string;
  name: string;
  category: string;
  location?: string | null;
  expiry_date: string | null;
  quantity: number;
  created_at: string;
  updated_at: string;
};

// Types for dashboard statistics
export type DashboardStats = {
  expiringSoon: number;
  recentlyExpired: number;
  totalInventory: number;
  averageShelfLife?: number; // New field: average remaining shelf life in days
};

export type CategoryDistribution = {
  name: string;
  amount: number;
  percentage?: number; // New field: percentage of total
};

export type ExpiryTrend = {
  name: string;
  value: number;
  date?: string; // Actual date for better tooltip display
};

export type InventoryHealth = {
  name: string;
  value: number;
  percentage?: number; // New field: percentage of total
  color?: string; // Color for the chart
};

export type LocationDistribution = {
  name: string;
  value: number;
  percentage?: number;
};

// Get items expiring soon (within the alert window)
export const getExpiringSoonItems = async (alertWindow: number): Promise<InventoryItem[]> => {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + alertWindow);
  
  const { data, error } = await supabase
    .from('inventory_items')
    .select('*')
    .gte('expiry_date', today.toISOString().split('T')[0])
    .lte('expiry_date', futureDate.toISOString().split('T')[0]);
    
  if (error) {
    console.error("Error fetching expiring soon items:", error);
    return [];
  }
  
  return data || [];
};

// Get recently expired items (within the last 7 days)
export const getRecentlyExpiredItems = async (): Promise<InventoryItem[]> => {
  const today = new Date();
  const pastDate = new Date();
  pastDate.setDate(today.getDate() - 7);
  
  const { data, error } = await supabase
    .from('inventory_items')
    .select('*')
    .gte('expiry_date', pastDate.toISOString().split('T')[0])
    .lt('expiry_date', today.toISOString().split('T')[0]);
    
  if (error) {
    console.error("Error fetching recently expired items:", error);
    return [];
  }
  
  return data || [];
};

// Get all inventory items
export const getAllInventoryItems = async (): Promise<InventoryItem[]> => {
  const { data, error } = await supabase
    .from('inventory_items')
    .select('*')
    .order('created_at', { ascending: false });
    
  if (error) {
    console.error("Error fetching all inventory items:", error);
    return [];
  }
  
  return data || [];
};

// Get dashboard statistics
export const getDashboardStats = async (alertWindow: number): Promise<DashboardStats> => {
  const expiringSoon = await getExpiringSoonItems(alertWindow);
  const recentlyExpired = await getRecentlyExpiredItems();
  const allItems = await getAllInventoryItems();
  
  // Calculate average shelf life for items with expiry dates
  let totalShelfLifeDays = 0;
  let itemsWithExpiry = 0;
  
  const today = new Date();
  
  allItems.forEach(item => {
    if (item.expiry_date) {
      const expiryDate = parseISO(item.expiry_date);
      const daysRemaining = differenceInDays(expiryDate, today);
      
      if (daysRemaining > 0) {
        totalShelfLifeDays += daysRemaining;
        itemsWithExpiry++;
      }
    }
  });
  
  const averageShelfLife = itemsWithExpiry > 0 
    ? Math.round(totalShelfLifeDays / itemsWithExpiry) 
    : 0;
  
  return {
    expiringSoon: expiringSoon.length,
    recentlyExpired: recentlyExpired.length,
    totalInventory: allItems.length,
    averageShelfLife
  };
};

// Get category distribution
export const getCategoryDistribution = async (): Promise<CategoryDistribution[]> => {
  const allItems = await getAllInventoryItems();
  
  const categoryMap = new Map<string, number>();
  
  allItems.forEach(item => {
    const category = item.category;
    categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
  });
  
  const total = allItems.length;
  
  return Array.from(categoryMap.entries())
    .map(([name, amount]) => ({
      name,
      amount,
      percentage: total > 0 ? Math.round((amount / total) * 100) : 0
    }))
    .sort((a, b) => b.amount - a.amount); // Sort by amount descending
};

// Get location distribution
export const getLocationDistribution = async (): Promise<LocationDistribution[]> => {
  const allItems = await getAllInventoryItems();
  
  const locationMap = new Map<string, number>();
  
  // Count items by location
  allItems.forEach(item => {
    const location = item.location || "Other";
    locationMap.set(location, (locationMap.get(location) || 0) + 1);
  });
  
  const total = allItems.length;
  
  // If we have no location data, return some default locations
  if (locationMap.size === 0 || (locationMap.size === 1 && locationMap.has("Other"))) {
    return [
      { name: "Pantry", value: 0, percentage: 0 },
      { name: "Refrigerator", value: 0, percentage: 0 },
      { name: "Freezer", value: 0, percentage: 0 },
      { name: "Medicine Cabinet", value: 0, percentage: 0 }
    ];
  }
  
  return Array.from(locationMap.entries())
    .map(([name, value]) => ({
      name,
      value,
      percentage: total > 0 ? Math.round((value / total) * 100) : 0
    }))
    .sort((a, b) => b.value - a.value); // Sort by value descending
};

// Get expiry trend for the past 3 weeks
export const getExpiryTrend = async (): Promise<ExpiryTrend[]> => {
  const allItems = await getAllInventoryItems();
  
  // Create data for the past 3 weeks
  const weeks = [];
  const today = new Date();
  
  for (let i = 3; i >= 0; i--) {
    const date = subDays(today, i * 7);
    const weekName = `Week ${3-i}`;
    const formattedDate = format(date, 'MMM d'); // Format date as "Jan 15"
    
    weeks.push({ 
      date: formattedDate, 
      fullDate: date,
      name: weekName, 
      value: 0 
    });
  }
  
  // Count items expiring in each week
  allItems.forEach(item => {
    if (item.expiry_date) {
      const expiryDate = parseISO(item.expiry_date);
      
      for (let i = 0; i < weeks.length - 1; i++) {
        const startDate = weeks[i].fullDate;
        const endDate = i < weeks.length - 1 ? weeks[i+1].fullDate : today;
        
        if (expiryDate >= startDate && expiryDate < endDate) {
          weeks[i].value += 1;
          break;
        }
      }
    }
  });
  
  return weeks.map(week => ({
    name: week.name,
    value: week.value,
    date: week.date
  }));
};

// Get inventory health with dynamic alert window
export const getInventoryHealth = async (alertWindow: number = 7): Promise<InventoryHealth[]> => {
  const today = new Date();
  const allItems = await getAllInventoryItems();
  
  let expired = 0;
  let expiringSoon = 0;
  let healthy = 0;
  
  allItems.forEach(item => {
    if (item.expiry_date) {
      const expiryDate = parseISO(item.expiry_date);
      const daysDifference = differenceInDays(expiryDate, today);
      
      if (daysDifference < 0) {
        expired += 1;
      } else if (daysDifference <= alertWindow) {
        expiringSoon += 1;
      } else {
        healthy += 1;
      }
    } else {
      healthy += 1;
    }
  });
  
  const total = allItems.length;
  
  return [
    { 
      name: "Expired", 
      value: expired, 
      percentage: total > 0 ? Math.round((expired / total) * 100) : 0,
      color: "#ff4d4f"
    },
    { 
      name: "Expiring Soon", 
      value: expiringSoon, 
      percentage: total > 0 ? Math.round((expiringSoon / total) * 100) : 0,
      color: "#faad14" 
    },
    { 
      name: "Healthy", 
      value: healthy, 
      percentage: total > 0 ? Math.round((healthy / total) * 100) : 0,
      color: "#52c41a"
    }
  ];
};
